import React, { useState, useEffect, useRef } from 'react';

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(24);
  const chatRef = useRef<HTMLDivElement>(null);

  // Adjust button position based on footer visibility
  useEffect(() => {
    const adjustPosition = () => {
      const footer = document.querySelector('footer');
      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const footerVisible = footerRect.top < windowHeight && footerRect.bottom > 0;
        
        if (footerVisible) {
          const distanceFromBottom = windowHeight - footerRect.top;
          setBottomOffset(distanceFromBottom + 24);
        } else {
          setBottomOffset(24);
        }
      }
    };

    window.addEventListener('scroll', adjustPosition);
    window.addEventListener('resize', adjustPosition);
    adjustPosition();
    return () => {
      window.removeEventListener('scroll', adjustPosition);
      window.removeEventListener('resize', adjustPosition);
    };
  }, []);

  // Close chat panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && chatRef.current && !chatRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleEmailClick = () => {
    window.location.href = 'mailto:support@bargad.ai';
  };

  return (
    <div ref={chatRef}>
      {/* Contact Options Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed right-3 sm:right-6 z-50 w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center group hover:scale-110 transition-all duration-300 rounded-full bg-[#26ab4d] border-2 border-[#26ab4d]"
        style={{ bottom: `${bottomOffset}px` }}
        aria-label="Contact options"
      >
        {isOpen ? (
          <svg className="w-6 h-6 sm:w-7 sm:h-7 text-[#ffffff] transition-transform duration-300 group-hover:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6 sm:w-7 sm:h-7 text-[#ffffff] transition-transform duration-300 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>

      {/* Contact Options Panel */}
      <div 
        className={`fixed right-3 sm:right-6 z-40 flex flex-col-reverse items-center space-y-reverse space-y-3 transition-all duration-300 ease-in-out ${
          isOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
        }`} 
        style={{ bottom: `${bottomOffset + 80}px` }}
      >
        {/* Email Button */}
        <button
          onClick={handleEmailClick}
          className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center hover:scale-110 transition-all duration-300 group"
          aria-label="Email us"
        >
          <svg className="w-6 h-6 sm:w-7 sm:h-7 text-[#26ab4d] group-hover:text-[#ffffff] transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Chatbot;
